import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, Tag, X } from 'lucide-react';
import { useCart } from '../lib/cart';
import { optimize } from '../lib/imageOptimizer';
import ProductImg from '../components/ProductImg';

export default function CartPage() {
  const {
    items, removeItem, updateQuantity, subtotal, total, count, promo,
    promoCode, promoCodeDiscount, promoError, applyPromoCode, removePromoCode,
  } = useCart();
  const [codeInput, setCodeInput] = useState('');
  const [applying, setApplying] = useState(false);

  const onApplyPromo = async (e) => {
    e.preventDefault();
    if (applying) return;
    setApplying(true);
    const ok = await applyPromoCode(codeInput);
    setApplying(false);
    if (ok) setCodeInput('');
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 text-center" data-testid="cart-empty">
        <h1 className="font-heading text-3xl font-bold uppercase mb-4 text-zinc-900">Votre panier est vide</h1>
        <p className="text-zinc-500 font-body mb-6">Découvrez notre collection de maillots</p>
        <Link to="/catalogue" className="btn-primary inline-block bg-zinc-900 text-white font-bold text-sm px-8 py-3 uppercase tracking-wider">
          Voir le catalogue
        </Link>
      </div>
    );
  }

  // Build a set of free item keys for marking
  const freeKeys = new Set();
  const freeCountMap = {};
  promo.freeItems.forEach(fi => {
    const key = `${fi.product_id}_${fi.size}_${fi.customization}`;
    freeCountMap[key] = (freeCountMap[key] || 0) + 1;
  });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="cart-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-6 text-zinc-900">
        Panier <span className="text-zinc-400 text-lg font-body font-normal">({count} article{count > 1 ? 's' : ''})</span>
      </h1>

      {/* Promo banner in cart */}
      {count >= 2 && (
        <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 px-4 py-3 mb-6 flex items-center gap-3" data-testid="cart-promo-banner">
          <Tag size={16} className="text-[#FF3B30] shrink-0" />
          <p className="text-sm font-body">
            {promo.freeCount > 0 ? (
              <span className="text-[#FF3B30] font-bold">Promo appliquée ! {promo.freeCount} maillot{promo.freeCount > 1 ? 's' : ''} offert{promo.freeCount > 1 ? 's' : ''} (-{promo.discount.toFixed(2)} €)</span>
            ) : (
              <span className="text-zinc-600">Ajoutez encore <strong>{3 - count} maillot{3 - count > 1 ? 's' : ''}</strong> pour bénéficier de l'offre <strong className="text-[#FF3B30]">2 achetés = 1 offert</strong></span>
            )}
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-0 divide-y divide-zinc-100">
          {items.map(item => {
            const itemKey = `${item.product_id}_${item.size}_${item.customization || ''}`;
            const freeQty = freeCountMap[itemKey] || 0;
            return (
              <div key={itemKey} className="flex gap-4 py-4" data-testid={`cart-item-${item.product_id}`}>
                <Link to={`/produit/${item.slug}`} className="w-20 h-20 sm:w-24 sm:h-24 bg-zinc-50 shrink-0 overflow-hidden">
                  <ProductImg src={item.image} alt={item.name} width={120} />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/produit/${item.slug}`} className="text-sm font-medium text-zinc-900 hover:underline block truncate">{item.name}</Link>
                  <p className="text-xs text-zinc-400 font-body mt-0.5">
                    Taille : {item.size}
                    {item.customization && <> &mdash; Perso : {item.customization}</>}
                  </p>
                  {freeQty > 0 && (
                    <span className="inline-block mt-1 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid="free-badge">
                      Offert
                    </span>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center border border-zinc-200">
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.customization, item.quantity - 1)} className="p-1.5 text-zinc-400 hover:text-zinc-900" data-testid="qty-minus">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center text-sm font-body text-zinc-900">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.customization, item.quantity + 1)} className="p-1.5 text-zinc-400 hover:text-zinc-900" data-testid="qty-plus">
                        <Plus size={14} />
                      </button>
                    </div>
                    <button onClick={() => removeItem(item.product_id, item.size, item.customization)} className="text-zinc-400 hover:text-[#FF3B30]" data-testid="remove-item">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold font-body text-zinc-900">{(item.price * item.quantity).toFixed(2)} €</p>
                  {item.quantity > 1 && <p className="text-xs text-zinc-400 font-body">{item.price.toFixed(2)} €/u</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="border border-zinc-200 p-6 sticky top-24" data-testid="cart-summary">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-4 text-zinc-900">Résumé</h3>

            <div className="space-y-2 text-sm font-body mb-4">
              <div className="flex justify-between text-zinc-500">
                <span>Sous-total</span>
                <span>{subtotal.toFixed(2)} €</span>
              </div>

              {promo.discount > 0 && (
                <div className="flex justify-between text-[#FF3B30] font-medium" data-testid="promo-discount">
                  <span>2 achetés = 1 offert</span>
                  <span>-{promo.discount.toFixed(2)} €</span>
                </div>
              )}

              {promoCode && promoCodeDiscount > 0 && (
                <div className="flex justify-between text-emerald-600 font-medium" data-testid="promo-code-discount">
                  <span className="flex items-center gap-1.5">
                    Code « {promoCode.code} »
                    <button
                      type="button"
                      onClick={removePromoCode}
                      className="text-zinc-400 hover:text-zinc-700 transition-colors"
                      data-testid="promo-code-remove"
                      aria-label="Retirer le code promo"
                    >
                      <X size={12} />
                    </button>
                  </span>
                  <span>-{promoCodeDiscount.toFixed(2)} €</span>
                </div>
              )}

              <div className="flex justify-between text-zinc-500">
                <span>Livraison</span>
                <span className="text-[#22c55e]">Offerte</span>
              </div>
            </div>

            {/* Promo code input */}
            {!promoCode && (
              <form onSubmit={onApplyPromo} className="mb-4" data-testid="promo-code-form">
                <label className="text-xs text-zinc-500 uppercase tracking-wider block mb-2 font-body">
                  Code promo
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="ex. WELCOME10"
                    className="flex-1 border border-zinc-200 px-3 py-2 text-sm font-body uppercase tracking-wider focus:outline-none focus:border-zinc-900"
                    data-testid="promo-code-input"
                    maxLength={40}
                  />
                  <button
                    type="submit"
                    disabled={applying || !codeInput.trim()}
                    className="bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 transition-colors"
                    data-testid="promo-code-apply"
                  >
                    {applying ? '...' : 'OK'}
                  </button>
                </div>
                {promoError && (
                  <p className="text-xs text-red-600 mt-2 font-body" data-testid="promo-code-error">{promoError}</p>
                )}
              </form>
            )}

            <div className="border-t border-zinc-200 pt-3 flex justify-between font-bold text-lg mb-6">
              <span>Total</span>
              <span data-testid="cart-total">{total.toFixed(2)} €</span>
            </div>

            <Link
              to="/checkout"
              className="btn-primary block w-full bg-zinc-900 text-white font-bold text-sm py-3 uppercase tracking-wider text-center"
              data-testid="checkout-button"
            >
              Passer commande
            </Link>
            <Link to="/catalogue" className="block text-center text-xs text-zinc-500 hover:text-zinc-900 transition-colors mt-4 font-body">
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
